import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, 
  Radio, Music, X, ChevronUp, ChevronDown, Shuffle, Repeat
} from 'lucide-react';
import { Slider } from './ui/slider';

// Global state for the music player - can be accessed from anywhere
let globalPlayerState = {
  currentTrack: null,
  isPlaying: false,
  volume: 0.7,
  isRadio: false,
  listeners: new Set()
};

// Function to update global state and notify listeners
const updateGlobalPlayer = (updates) => {
  globalPlayerState = { ...globalPlayerState, ...updates };
  globalPlayerState.listeners.forEach(fn => fn(globalPlayerState));
};

// Hook to subscribe to global player state
export const useGlobalPlayer = () => {
  const [state, setState] = useState(globalPlayerState);
  
  useEffect(() => {
    const listener = (newState) => setState({ ...newState });
    globalPlayerState.listeners.add(listener);
    return () => globalPlayerState.listeners.delete(listener);
  }, []);
  
  return state;
};

// Function to play a track from anywhere in the app
export const playTrack = (track, isRadio = false) => {
  updateGlobalPlayer({
    currentTrack: track,
    isPlaying: true,
    isRadio
  });
};

// Function to play/pause
export const togglePlay = () => {
  updateGlobalPlayer({ isPlaying: !globalPlayerState.isPlaying });
};

// Function to stop playback
export const stopPlayback = () => {
  updateGlobalPlayer({
    currentTrack: null,
    isPlaying: false,
    isRadio: false
  });
};

// Function to set volume
export const setGlobalVolume = (volume) => {
  updateGlobalPlayer({ volume });
};

const GlobalMusicPlayer = ({ theme }) => {
  const audioRef = useRef(null);
  const playerState = useGlobalPlayer();
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [localVolume, setLocalVolume] = useState(playerState.volume);
  
  const { currentTrack, isPlaying, isRadio, volume } = playerState;
  
  // Update audio element when track changes
  useEffect(() => {
    if (audioRef.current && currentTrack) {
      let audioUrl = currentTrack.audio_url || currentTrack.stream_url || currentTrack.url;
      
      // Handle case where audio_url is a JSON object (stems result)
      if (typeof audioUrl === 'object' && audioUrl !== null) {
        // For stems, prefer instrumental, then vocals, then any first key
        audioUrl = audioUrl.instrumental || audioUrl.vocals || Object.values(audioUrl)[0];
      }
      
      // Handle case where audio_url is a JSON string
      if (typeof audioUrl === 'string' && audioUrl.startsWith('{')) {
        try {
          const parsed = JSON.parse(audioUrl);
          audioUrl = parsed.instrumental || parsed.vocals || Object.values(parsed)[0];
        } catch (e) {
          console.error('Failed to parse audio_url JSON:', e);
        }
      }
      
      if (audioUrl && audioRef.current.src !== audioUrl) {
        audioRef.current.src = audioUrl;
        audioRef.current.load();
      }
      if (isPlaying) {
        audioRef.current.play().catch(console.error);
      }
    }
  }, [currentTrack]);
  
  // Update play/pause state
  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch(console.error);
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);
  
  // Update volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
    setLocalVolume(volume);
  }, [volume, isMuted]);
  
  // Handle time update
  const handleTimeUpdate = () => {
    if (audioRef.current && !isRadio) {
      setProgress(audioRef.current.currentTime);
      setDuration(audioRef.current.duration || 0);
    }
  };
  
  // Handle seek
  const handleSeek = (value) => {
    if (audioRef.current && !isRadio) {
      audioRef.current.currentTime = value[0];
      setProgress(value[0]);
    }
  };
  
  // Handle volume change
  const handleVolumeChange = (value) => {
    const newVolume = value[0];
    setGlobalVolume(newVolume);
    setIsMuted(false);
  };
  
  // Toggle mute
  const handleToggleMute = () => {
    setIsMuted(!isMuted);
  };
  
  // Format time
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Don't render if no track
  if (!currentTrack) return null;
  
  const trackTitle = currentTrack.title || currentTrack.name || 'Unknown Track';
  const trackArtist = currentTrack.artist || currentTrack.created_by || 'Unknown Artist';
  const albumCover = currentTrack.album_cover || currentTrack.cover_image || currentTrack.thumbnail;
  
  return (
    <>
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => updateGlobalPlayer({ isPlaying: false })}
        onError={(e) => console.error('Audio error:', e)}
      />
      
      <AnimatePresence>
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          exit={{ y: 100 }}
          className="fixed bottom-0 left-0 right-0 z-50"
          style={{
            background: 'linear-gradient(to top, rgba(0,0,0,0.98), rgba(0,0,0,0.9))',
            borderTop: `1px solid ${theme?.primary || '#00f0ff'}33`,
            backdropFilter: 'blur(20px)'
          }}
        >
          {/* Progress bar at top (full width, thin) */}
          {!isRadio && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-white/10">
              <motion.div
                className="h-full"
                style={{ 
                  backgroundColor: theme?.primary || '#00f0ff',
                  width: `${(progress / duration) * 100 || 0}%`
                }}
              />
            </div>
          )}
          
          <div className={`transition-all duration-300 ${isMinimized ? 'h-14' : 'h-16 sm:h-20'}`}>
            <div className="max-w-screen-2xl mx-auto px-2 sm:px-4 h-full flex items-center justify-between gap-2 sm:gap-4">
              
              {/* Left: Track Info */}
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-shrink max-w-[40%] sm:max-w-[300px]">
                {albumCover ? (
                  <img 
                    src={albumCover} 
                    alt={trackTitle}
                    className={`rounded-lg object-cover shadow-lg transition-all ${isMinimized ? 'w-10 h-10' : 'w-14 h-14'}`}
                  />
                ) : (
                  <div 
                    className={`rounded-lg flex items-center justify-center transition-all ${isMinimized ? 'w-10 h-10' : 'w-14 h-14'}`}
                    style={{ backgroundColor: `${theme?.primary || '#00f0ff'}20` }}
                  >
                    {isRadio ? (
                      <Radio className="w-6 h-6" style={{ color: theme?.primary || '#00f0ff' }} />
                    ) : (
                      <Music className="w-6 h-6" style={{ color: theme?.primary || '#00f0ff' }} />
                    )}
                  </div>
                )}
                <div className="overflow-hidden">
                  <p className="font-bold text-white truncate text-sm">
                    {trackTitle}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {isRadio ? '🔴 Live Radio' : trackArtist}
                  </p>
                </div>
              </div>
              
              {/* Center: Controls */}
              <div className="flex flex-col items-center gap-1 flex-1 min-w-0 max-w-[600px]">
                <div className="flex items-center gap-2 sm:gap-4">
                  <button 
                    className="hidden sm:block p-2 text-gray-400 hover:text-white transition opacity-50 cursor-not-allowed"
                    disabled
                  >
                    <Shuffle className="w-4 h-4" />
                  </button>
                  <button 
                    className="p-1 sm:p-2 text-gray-400 hover:text-white transition opacity-50 cursor-not-allowed"
                    disabled
                  >
                    <SkipBack className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  
                  <button
                    onClick={togglePlay}
                    className="p-2 sm:p-3 rounded-full transition-all hover:scale-105"
                    style={{ backgroundColor: theme?.primary || '#00f0ff' }}
                  >
                    {isPlaying ? (
                      <Pause className="w-5 h-5 text-black" fill="black" />
                    ) : (
                      <Play className="w-5 h-5 text-black" fill="black" />
                    )}
                  </button>
                  
                  <button 
                    className="p-1 sm:p-2 text-gray-400 hover:text-white transition opacity-50 cursor-not-allowed"
                    disabled
                  >
                    <SkipForward className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  <button 
                    className="hidden sm:block p-2 text-gray-400 hover:text-white transition opacity-50 cursor-not-allowed"
                    disabled
                  >
                    <Repeat className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Progress slider (only for non-radio) */}
                {!isMinimized && !isRadio && (
                  <div className="flex items-center gap-2 w-full">
                    <span className="text-xs text-gray-400 w-10 text-right">
                      {formatTime(progress)}
                    </span>
                    <Slider
                      value={[progress]}
                      max={duration || 100}
                      step={1}
                      onValueChange={handleSeek}
                      className="flex-1"
                    />
                    <span className="text-xs text-gray-400 w-10">
                      {formatTime(duration)}
                    </span>
                  </div>
                )}
                
                {/* Radio indicator */}
                {isRadio && !isMinimized && (
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-xs text-gray-400">Live Stream</span>
                  </div>
                )}
              </div>
              
              {/* Right: Volume & Actions */}
              <div className="flex items-center gap-1 sm:gap-3 min-w-0 flex-shrink-0 justify-end">
                <button
                  onClick={handleToggleMute}
                  className="p-2 text-gray-400 hover:text-white transition"
                >
                  {isMuted || volume === 0 ? (
                    <VolumeX className="w-5 h-5" />
                  ) : (
                    <Volume2 className="w-5 h-5" />
                  )}
                </button>
                
                {!isMinimized && (
                  <div className="hidden sm:block w-24">
                    <Slider
                      value={[isMuted ? 0 : localVolume]}
                      max={1}
                      step={0.01}
                      onValueChange={handleVolumeChange}
                    />
                  </div>
                )}
                
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-2 text-gray-400 hover:text-white transition"
                >
                  {isMinimized ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                
                <button
                  onClick={stopPlayback}
                  className="p-2 text-gray-400 hover:text-red-400 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

export default GlobalMusicPlayer;
